import { formatDateToLocale } from "@/common/helpers/format-date";
import { cn } from "@/common/helpers/utils";
import { EMPTY_VALUE_PLACEHOLDER } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CircleHelpIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { resolveVehicleValuationDate, toLocalIsoDate } from "./valuation-date";

interface VehicleValuationProjectedMileageIndicatorProps {
  mileage?: number | null;
  projectedMileage?: number | null;
  targetDate?: Date | string | null;
  className?: string;
}

export function VehicleValuationProjectedMileageIndicator({
  mileage,
  projectedMileage,
  targetDate,
  className,
}: VehicleValuationProjectedMileageIndicatorProps) {
  const { t, i18n } = useTranslation("fab");
  const resolvedTargetDate = resolveVehicleValuationDate(targetDate);

  if (typeof mileage !== "number" || typeof projectedMileage !== "number" || projectedMileage === mileage) {
    return null;
  }

  const formatInteger = (value: number): string => new Intl.NumberFormat(i18n.language).format(value);
  const targetDateText = resolvedTargetDate
    ? formatDateToLocale(toLocalIsoDate(resolvedTargetDate), { dateOnly: true })
    : EMPTY_VALUE_PLACEHOLDER;
  const tooltipText = t("fab:VehicleValuationProjectedMileageTooltip", {
    mileage: formatInteger(mileage),
    projectedMileage: formatInteger(projectedMileage),
    targetDate: targetDateText,
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center text-sky-600 dark:text-sky-400",
            className,
          )}
          role="img"
          aria-label={tooltipText}
        >
          <CircleHelpIcon className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1">
        <p className="font-medium">{t("fab:VehicleValuationProjectedMileage")}</p>
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}
